/**
 * Linmere New York (20 Oct 2026): two things the "Dressed for the city" cart drawer adds to Horizon's cart
 * (styles in assets/lnm-city-cart.css, loaded from snippets/cart-drawer.liquid):
 * - "Removed <product>. Undo": when a product is removed from the drawer, a line offers to put it back. It is
 *   written into [data-lnm-cart-undo] slots, which Horizon's re-render leaves alone (data-skip-subtree-update)
 *   or brings back empty (the empty-cart markup), so it is filled again after every re-render. It goes away
 *   when the drawer closes or another product is added. Undo adds the same variant and quantity with the
 *   standard cart event, so the drawer re-renders and shipping protection follows its usual rules.
 * - On computers Horizon opens the drawer without a backdrop: .lnm-cc-scrim dims the page and closes the
 *   drawer when clicked, like the backdrop on phones.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const drawer = () => /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));

/** @type {{ variantId: string, quantity: number, title: string } | null} */
let pending = null;
let restoring = false;
let painting = false;

function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/* ---------- undo ---------- */
function paint(focus) {
  const root = drawer();
  if (!root) return;
  painting = true;
  const html = pending
    ? `<p>Removed ${escapeHtml(pending.title)}.</p><button type="button" data-lnm-undo-btn>Undo</button>`
    : '';
  root.querySelectorAll('[data-lnm-cart-undo]').forEach((slot) => {
    if (slot.innerHTML !== html) slot.innerHTML = html;
  });
  painting = false;
  if (focus && pending) {
    const active = document.activeElement;
    // only when the removed row took the focus with it (never pull it out of another dialog)
    const lost = !active || active === document.body;
    const button = root.querySelector('[data-lnm-undo-btn]');
    if (lost && button) button.focus({ preventScroll: true });
  }
}

function clearUndo() {
  if (!pending) return;
  pending = null;
  paint(false);
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function undo() {
  const item = pending;
  const root = drawer();
  if (!item || !root || restoring) return;
  restoring = true;
  pending = null;
  paint(false);

  const routes = (window.Theme && window.Theme.routes) || {};
  const deferred = CartLinesUpdateEvent.createPromise();
  deferred.promise.catch(() => {});
  root.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: item.variantId, quantity: item.quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(item.variantId), quantity: item.quantity }],
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    }).then((r) => r.json());
    if (response && typeof response.status === 'number' && response.status >= 400) {
      throw new Error(response.description || response.message || 'Unable to add it back. Try again.');
    }
    const cart = await fetch((routes.cart_url || '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: response.sections ?? undefined,
        source: 'lnm-city-cart',
        didError: false,
      },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to add it back. Try again.';
    deferred.reject(error);
    root.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
    pending = item;
    paint(true);
  } finally {
    restoring = false;
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const update = /** @type {any} */ (event);
  const root = drawer();
  if (!root) return;

  if (update.action === 'remove') {
    if (!(event.target instanceof Node) || !root.contains(event.target)) return;
    const line = Array.isArray(update.lines) ? update.lines[0] : null;
    if (!line || !line.id) return;
    const row = root.querySelector(`.cart-items__table-row[data-key="${CSS.escape(String(line.id))}"]`);
    if (!row || row.classList.contains('lnm-protection-line')) return;
    const input = row.querySelector('input[type="number"]');
    const quantity = Math.max(1, parseInt(input?.getAttribute('value') || '1', 10) || 1);
    const title = row.querySelector('.cart-items__title')?.textContent?.trim() || 'the item';
    const item = { variantId: String(line.id).split(':')[0], quantity, title };
    update.promise
      ?.then((result) => {
        if (result?.detail?.didError) return;
        pending = item;
        // the row leaves with Horizon's own animation; the line goes in its own slot meanwhile
        setTimeout(() => paint(true), 0);
      })
      .catch(() => {});
    return;
  }

  if (update.action === 'add' && !restoring) clearUndo();
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (target?.closest('[data-lnm-undo-btn]')) {
    event.preventDefault();
    undo();
  }
});

/* re-renders can bring the slots back empty: fill them again */
function watchDrawer(root) {
  const observer = new MutationObserver(() => {
    if (painting || !pending) return;
    const empty = Array.from(root.querySelectorAll('[data-lnm-cart-undo]')).some((slot) => slot.innerHTML === '');
    if (empty) paint(false);
  });
  observer.observe(root, { childList: true, subtree: true });
}

/* ---------- computers: dim the page behind the drawer ---------- */
const wide = window.matchMedia('(min-width: 990px)');
let scrim = null;

function ensureScrim() {
  if (scrim) return scrim;
  scrim = document.createElement('div');
  scrim.className = 'lnm-cc-scrim';
  scrim.setAttribute('aria-hidden', 'true');
  scrim.addEventListener('click', () => drawer()?.close?.());
  document.body.appendChild(scrim);
  return scrim;
}

function showScrim(instant) {
  const root = drawer();
  if (!root || !wide.matches) return;
  const layer = ensureScrim();
  if (instant) layer.style.transition = 'none';
  void layer.offsetWidth; // a new layer starts from its hidden state, so the fade runs
  layer.classList.add('is-on');
  if (instant) {
    void layer.offsetWidth;
    layer.style.removeProperty('transition');
  }
}

function hideScrim() {
  scrim?.classList.remove('is-on');
}

function init() {
  const root = drawer();
  if (!root) return;
  watchDrawer(root);
  root.addEventListener('theme-drawer:open', () => showScrim(false));
  root.addEventListener('theme-drawer:close', () => {
    hideScrim();
    clearUndo();
  });
  wide.addEventListener('change', () => {
    if (root.hasAttribute('open') && wide.matches) showScrim(true);
    else hideScrim();
  });
  // a drawer left open on the previous page is reopened before this file runs
  if (root.hasAttribute('open')) showScrim(true);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
